/**
 * Company Settings Configuration Page
 *
 * Allows administrators to configure:
 * - Company branding (name, tagline, logo)
 * - Email domain configuration
 * - Support contact information
 * - Multiple domain support
 */

import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Row,
  Col,
  Divider,
  message,
  Spin,
  Typography,
  Upload,
  Space,
  Tag,
} from "antd";
import {
  SaveOutlined,
  ReloadOutlined,
  UploadOutlined,
  InfoCircleOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import Axios from "axios";
import StatCard from '@/components/StatCard';
import { Building2, Mail, Globe, CheckCircle } from "lucide-react";
import { useTheme } from '@/App';
import { defaultColors } from '@/themes/antd-theme';
import Icons from '@/components/third-party/Icons';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const CompanySettings = () => {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const textColor = isDark ? '#ffffff' : '#1f2937';
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  // Dark mode colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : 'rgba(0, 0, 0, 0.45)';
  const infoBgColor = isDark ? defaultColors.palette.gray[700] : '#f5f5f5';
  const notesBgColor = isDark ? 'rgba(24, 144, 255, 0.15)' : '#e6f7ff';

  // Fetch company settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("Token");
      const response = await Axios.get("/bp/company-settings", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        const data = response.data.data;
        setSettings(data);
        form.setFieldsValue({
          company_name: data.company_name,
          company_short_name: data.company_short_name,
          company_tagline: data.company_tagline,
          primary_email_domain: data.primary_email_domain,
          allowed_email_domains: data.allowed_email_domains,
          support_email: data.support_email,
          info_email: data.info_email,
          company_website: data.company_website,
          company_address: data.company_address,
          company_phone: data.company_phone,
        });
      } else {
        toast.error("Failed to fetch company settings");
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error("Error fetching company settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const token = localStorage.getItem("Token");
      const response = await Axios.post(
        "/bp/company-settings/update",
        values,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.status === "success") {
        toast.success("Company settings updated successfully");
        fetchSettings(); // Refresh data
      } else {
        toast.error(response.data.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Error saving company settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    const formData = new FormData();
    formData.append("logo", file);

    try {
      const token = localStorage.getItem("Token");
      const response = await fetch("/api/company-settings/upload-logo", {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json();

      if (data.status === "success") {
        toast.success("Company logo uploaded successfully");
        fetchSettings(); // Refresh to show new logo
      } else {
        toast.error(data.message || "Failed to upload logo");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Error uploading company logo");
    }

    return false; // Prevent default upload behavior
  };

  const handleReset = () => {
    if (settings) {
      form.setFieldsValue({
        company_name: settings.company_name,
        company_short_name: settings.company_short_name,
        company_tagline: settings.company_tagline,
        primary_email_domain: settings.primary_email_domain,
        allowed_email_domains: settings.allowed_email_domains,
        support_email: settings.support_email,
        info_email: settings.info_email,
        company_website: settings.company_website,
        company_address: settings.company_address,
        company_phone: settings.company_phone,
      });
      message.info("Form reset to saved values");
    }
  };

  if (loading && !settings) {
    return (
      <Card style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Spin size="large" tip="Loading company settings..." />
      </Card>
    );
  }

  return (
    <Card
      className="m-2.5 border border-gray-300 shadow-md"
      title={
        <div className="flex items-center gap-2">
          <Icons.Building2 className="w-7 h-7 text-blue-600" />
          <Title level={4} style={{ color: textColor, margin: 0, padding: 0 }}>
            {t('Company Settings')}
          </Title>
        </div>
      }
      style={{ backgroundColor: cardBgColor }}
    >
      <Paragraph style={{ color: secondaryTextColor, marginBottom: 24 }}>
        Configure company branding, email domains, and contact information
      </Paragraph>

      {/* Top Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <StatCard
            label="Company Profile"
            value={settings?.company_name ? "Configured" : "Pending"}
            icon={<Building2 size={24} />}
            bgColor={isDark ? '#1e3a5f' : '#dbeafe'}
            iconColor={isDark ? '#60a5fa' : '#3b82f6'}
          />
        </Col>
        <Col span={6}>
          <StatCard
            label="Email Domains"
            value={(settings?.allowed_email_domains?.split(',')?.length || 0) + 1}
            icon={<Mail size={24} />}
            bgColor={isDark ? '#4c1d95' : '#f3e8ff'}
            iconColor={isDark ? '#c084fc' : '#a855f7'}
          />
        </Col>
        <Col span={6}>
          <StatCard
            label="Website Status"
            value={settings?.company_website ? "Active" : "Not Set"}
            icon={<Globe size={24} />}
            bgColor={isDark ? '#713f12' : '#fef9c3'}
            iconColor={isDark ? '#fbbf24' : '#facc15'}
          />
        </Col>
        <Col span={6}>
          <StatCard
            label="Configuration Status"
            value={settings?.company_name && settings?.primary_email_domain ? "Complete" : "Incomplete"}
            icon={<CheckCircle size={24} />}
            bgColor={isDark ? '#14532d' : '#d1fae5'}
            iconColor={isDark ? '#4ade80' : '#10b981'}
          />
        </Col>
      </Row>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={{
          company_name: "Quantum Pharmaceuticals",
          company_short_name: "QuantumRx",
          primary_email_domain: "quantumrx.com",
        }}
      >
        {/* Company Branding Section */}
        <Divider orientation="left">
          <Space>
            <GlobalOutlined />
            <Text strong>Company Branding</Text>
          </Space>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Company Name (Legal)"
              name="company_name"
              rules={[
                { required: true, message: "Please enter company name" },
              ]}
              tooltip="Full legal company name"
            >
              <Input placeholder="e.g., Quantum Pharmaceuticals" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Short Name (Brand)"
              name="company_short_name"
              rules={[
                { required: true, message: "Please enter short name" },
              ]}
              tooltip="Brand name used in UI"
            >
              <Input placeholder="e.g., QuantumRx" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item
              label="Company Tagline"
              name="company_tagline"
              tooltip="Company tagline or motto"
            >
              <Input placeholder="e.g., Precision Medicine, Quantum Results" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Company Logo"
              tooltip="Upload company logo (PNG, JPG, SVG)"
            >
              <Upload
                beforeUpload={handleLogoUpload}
                accept=".png,.jpg,.jpeg,.svg,.gif"
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />}>Upload Logo</Button>
              </Upload>
              {settings?.company_logo && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={settings.company_logo}
                    alt="Company Logo"
                    style={{ maxHeight: 60, marginTop: 8 }}
                  />
                </div>
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* Email Domain Configuration Section */}
        <Divider orientation="left">
          <Space>
            <MailOutlined />
            <Text strong>Email Domain Configuration</Text>
          </Space>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Primary Email Domain"
              name="primary_email_domain"
              rules={[
                { required: true, message: "Please enter primary email domain" },
                {
                  pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/,
                  message: "Please enter a valid domain (without @)",
                },
              ]}
              tooltip="Primary email domain without @ symbol"
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <InfoCircleOutlined /> Used for auto-generating user email addresses
                </Text>
              }
            >
              <Input
                prefix="@"
                placeholder="quantumrx.com"
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Allowed Email Domains"
              name="allowed_email_domains"
              tooltip="Comma-separated list of allowed domains for multi-domain organizations"
              extra={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  <InfoCircleOutlined /> For subsidiaries: domain1.com, domain2.com, domain3.com
                </Text>
              }
            >
              <Input placeholder="subsidiary1.com, subsidiary2.com" />
            </Form.Item>
          </Col>
        </Row>

        {/* Contact Information Section */}
        <Divider orientation="left">
          <Space>
            <PhoneOutlined />
            <Text strong>Contact Information</Text>
          </Space>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Support Email"
              name="support_email"
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email",
                },
              ]}
              tooltip="Customer support email address"
            >
              <Input placeholder="support@quantumrx.com" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Info Email"
              name="info_email"
              rules={[
                {
                  type: "email",
                  message: "Please enter a valid email",
                },
              ]}
              tooltip="General information email address"
            >
              <Input placeholder="info@quantumrx.com" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Company Website"
              name="company_website"
              rules={[
                {
                  type: "url",
                  message: "Please enter a valid URL",
                },
              ]}
            >
              <Input placeholder="https://www.quantumrx.com" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Company Phone" name="company_phone">
              <Input placeholder="+1 (555) 123-4567" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24}>
            <Form.Item label="Company Address" name="company_address">
              <TextArea
                rows={3}
                placeholder="123 Innovation Drive, Precision Valley, CA 94000, USA"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Action Buttons */}
        <Divider />

        <Row gutter={16} justify="end">
          <Col>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              htmlType="submit"
              loading={saving}
            >
              Save Settings
            </Button>
          </Col>
        </Row>

        {/* Info Section */}
        {settings && (
          <div style={{ marginTop: 24, padding: 16, background: infoBgColor, borderRadius: 4 }}>
            <Text style={{ fontSize: 12, color: secondaryTextColor }}>
              Last updated: {settings.updated_at || "Never"}
              {settings.updated_by && ` by ${settings.updated_by}`}
            </Text>
          </div>
        )}
      </Form>

      {/* Information Panel */}
      <div style={{ marginTop: 24, padding: 16, background: notesBgColor, borderRadius: 4 }}>
        <Space direction="vertical" size="small">
          <Text strong>
            <InfoCircleOutlined /> Configuration Notes
          </Text>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>
              <Text style={{ color: secondaryTextColor }}>
                Primary email domain is used for auto-generating user email addresses
              </Text>
            </li>
            <li>
              <Text style={{ color: secondaryTextColor }}>
                Allowed email domains supports multiple domains for organizations with subsidiaries
              </Text>
            </li>
            <li>
              <Text style={{ color: secondaryTextColor }}>
                Changes take effect immediately for new users and email generation
              </Text>
            </li>
            <li>
              <Text style={{ color: secondaryTextColor }}>
                Existing user email addresses are not automatically updated
              </Text>
            </li>
          </ul>
        </Space>
      </div>
    </Card>
  );
};

export default CompanySettings;
